/**
 * SOLID Review
 * S — Strong. One concern: declare the sockets ComponentFrontendInfrastructure requires — the wire
 *     transport, and the Application's delivery port. No state, no lifecycle, no `connectAll`.
 * O — Strong. A new socket is one field here and one connector in `FrontendConnectors`.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. Every field is a `RequiredPortWithDefault<T>`.
 * D — Strong. The kernel, the transport client interfaces with their Null objects, and the
 *     Application's delivery contract.
 */
import { PortHolder } from '@ml3d/kernel';
import { RequiredPortWithDefault } from '@ml3d/kernel/required-with-default';
import type { IPortGraphQLDomainClient, IPortRestApiClient } from '../contract/ports';
import { NullGraphQLDomainClient, NullRestApiClient } from '../contract/clients';
import { ClientServerEventsContract, type IPortClientServerEvents } from '../../application/ports/ClientServerEvents.contract';
import { unavailable } from '../../application/ports/unavailable';

/**
 * ONE Null client per protocol, shared by every socket of that protocol. A Null object holds no state,
 * and sharing puts the `as unknown as` in ONE place: `IPortGraphQLDomainClient` is a `Pick<ApolloClient,
 * …>`, and implementing it honestly would mean reproducing Apollo's generic result types for an object
 * whose purpose is to do nothing.
 */
const NULL_GRAPHQL_CLIENT = new NullGraphQLDomainClient() as unknown as IPortGraphQLDomainClient;
const NULL_REST_CLIENT: IPortRestApiClient = new NullRestApiClient();

/**
 * Every socket ComponentFrontendInfrastructure requires (frontend strict-ports plan, FE1d, 2026-10-03)
 * — the twin of the backend's `ComponentInfrastructureRequiredPorts`.
 *
 * THE EIGHT WIRE SOCKETS — the client half of the client↔server seam, named after the backend lollipops
 * they pair with BY NAME in the system diagram (`ports:check`'s WIRE_TRANSPORT list: they carry the
 * transport client, and `@ml3d/contract` governs the wire). One Apollo client fills all six GraphQL
 * sockets and one REST client both REST sockets: they are independently ENUMERABLE and breakable, not
 * independently substitutable. Renamed `*Port` → `*Socket` with the backend's convention (2026-09-07);
 * the port NAMES are unchanged, so the pairing is too.
 *
 * THE DELIVERY SOCKET — where the server's events go: the Application's `ClientServerEvents` lollipop,
 * connected BEFORE `build()` so the watch adapter built there holds a wired port.
 *
 * Basename starts with `Component` and every constructor carries its `<T>`, or `lint:explained-uml`
 * stops seeing the names (§28.105). Walked because this class extends `PortHolder`.
 */
export class ComponentFrontendInfrastructureRequiredPorts extends PortHolder {
  readonly graphQLRoomDomainSocket = new RequiredPortWithDefault<IPortGraphQLDomainClient>('GraphQLRoomDomain', NULL_GRAPHQL_CLIENT);
  readonly graphQLRoundDomainSocket = new RequiredPortWithDefault<IPortGraphQLDomainClient>('GraphQLRoundDomain', NULL_GRAPHQL_CLIENT);
  readonly graphQLPlayerDomainSocket = new RequiredPortWithDefault<IPortGraphQLDomainClient>('GraphQLPlayerDomain', NULL_GRAPHQL_CLIENT);
  readonly graphQLMediaDomainSocket = new RequiredPortWithDefault<IPortGraphQLDomainClient>('GraphQLMediaDomain', NULL_GRAPHQL_CLIENT);
  readonly graphQLAdsDomainSocket = new RequiredPortWithDefault<IPortGraphQLDomainClient>('GraphQLAdsDomain', NULL_GRAPHQL_CLIENT);
  readonly graphQLPaymentDomainSocket = new RequiredPortWithDefault<IPortGraphQLDomainClient>('GraphQLPaymentDomain', NULL_GRAPHQL_CLIENT);
  readonly restAuthApiSocket = new RequiredPortWithDefault<IPortRestApiClient>('RestAuthApi', NULL_REST_CLIENT);
  readonly restLegalApiSocket = new RequiredPortWithDefault<IPortRestApiClient>('RestLegalApi', NULL_REST_CLIENT);
  readonly clientServerEventsSocket = new RequiredPortWithDefault<IPortClientServerEvents>(
    'ClientServerEvents', unavailable<IPortClientServerEvents>(ClientServerEventsContract), ClientServerEventsContract);
}
